import { PayOverTimeDetailsResultTypenames } from '@varo/graphql-schema-types';
import { NativeStackHeaderProps, Stack, useRouter } from 'expo-router';
import { useFocusEffect } from 'expo-router/react-navigation';
import { resolveErrorCopy } from 'features/payOverTime/helpers/PayOverTime.helpers';
import CurrentLoansTab from 'features/payOverTime/screens/ManageLoans/CurrentLoanTab/CurrentLoansTab';
import HistoryTab from 'features/payOverTime/screens/ManageLoans/HistoryTab/HistoryTab';
import usePayOverTimeDetails from 'features/payOverTime/screens/ManageLoans/hooks/usePayOverTimeDetails';
import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ScrollView, StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import IconButton from 'shared/components/vxl/icon/IconButton/IconButton';
import ListItem from 'shared/components/vxl/ListItem/ListItem';
import FullScreenLoading from 'shared/components/vxl/loading/FullScreenLoading/FullScreenLoading';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import PillGroup from 'shared/components/vxl/navigation/PillGroup/PillGroup';
import ExpressiveSmText from 'shared/components/vxl/typography/expressive/ExpressiveSmText';
import BodySmBoldText from 'shared/components/vxl/typography/primary/body/BodySmBoldText';
import { useViewDidFocusScreenAnalyticsEvent } from 'shared/hooks/analytics/useViewDidFocusAnalytics';
import useDismissToRootTab, {
  Tabs,
} from 'shared/hooks/useDismissToRootTab/useDismissToRootTab';
import ErrorScreen, {
  ErrorScreenType,
} from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import { VXLComponentId } from 'shared/renderer/types';
import theme from 'shared/styles/theme';

interface ExternalTypeTabProps {
  id: string;
  labelText: string;
}

const payOverTimeTypePillsData = [
  { id: 'activeLoans', labelText: 'Active plans' },
  { id: 'history', labelText: 'History' },
];

export const activeLoansPill = payOverTimeTypePillsData[0].id;
export const historyPill = payOverTimeTypePillsData[1].id;

const ManageLoans = () => {
  const router = useRouter();
  const { bottom } = useSafeAreaInsets();

  const {
    resultsObj: { outstandingBalance, openLoans, closedLoans, openLoansCount },
    error,
    fetching,
    refetch,
  } = usePayOverTimeDetails();

  const hasFocusedBefore = useRef(false);

  useFocusEffect(
    useCallback(() => {
      if (hasFocusedBefore.current) {
        refetch();
      } else {
        hasFocusedBefore.current = true;
      }
    }, [refetch]),
  );

  const [selectedTab, setSelectedTab] = useState(activeLoansPill);

  const { t: translatedText } = useTranslation('payOverTime', {
    keyPrefix: 'manageLoans',
  });

  useViewDidFocusScreenAnalyticsEvent({
    key: '/pay-over-time/manage-loans',
  });

  const dismissToTab = useDismissToRootTab();

  const detailsError =
    error &&
    '__typename' in error &&
    error.__typename ===
      PayOverTimeDetailsResultTypenames.PayOverTimeDetailsError
      ? error
      : null;

  const goHome = () => {
    dismissToTab(Tabs.HOME);
  };

  return (
    <View style={[styles.container, { paddingBottom: bottom }]}>
      <ScreenTransitionOverlay
        isLoading={fetching}
        LoadingComponent={<FullScreenLoading />}
        ErrorComponent={
          error ? (
            <ErrorScreen
              screenType={ErrorScreenType.Error}
              messageHeadline={resolveErrorCopy(
                detailsError?.title,
                translatedText('error.title'),
              )}
              messageBody={resolveErrorCopy(
                detailsError?.message,
                translatedText('error.body'),
              )}
              primaryButton={{
                title: translatedText('error.button'),
                onPress: goHome,
              }}
              error={error}
              insetBottomSafeArea={false}
            />
          ) : null
        }
      />
      <ScrollView style={styles.container}>
        <Stack.Screen
          options={{
            title: translatedText('title'),
            headerBackVisible: !error,
            headerShown: !error,
            gestureEnabled: true,
            header: (props: NativeStackHeaderProps) => (
              <ExpoNavigationBar
                trailingAccessories={
                  <IconButton
                    iconKey="SETTINGS"
                    iconSize="MD"
                    iconButtonAccessibilityLabel="SETTINGS"
                    iconOnPress={() =>
                      router.navigate('/pay-over-time/account-settings')
                    }
                  />
                }
                {...props}
              />
            ),
          }}
        />

        <View>
          <View style={styles.balanceContainer}>
            <BodySmBoldText data-testId="balanceTitle" numberOfLines={0}>
              {translatedText('totalOwed')}
            </BodySmBoldText>
            <ExpressiveSmText data-testId="balance" numberOfLines={0}>
              {outstandingBalance?.formattedWithPlusPrefix ?? '$0'}
            </ExpressiveSmText>
          </View>
          <ListItem
            primaryCopy={translatedText('payOverTimePlans')}
            showsChevron={false}
            trailingAccessory={{
              id: 'activeLoans',
              name: VXLComponentId.LIST_ITEM_TEXT_STACK,
              props: {
                primaryCopy: String(openLoansCount ?? 0),
              },
            }}
          />
          <View style={styles.sectionDivider} />
        </View>

        <View style={styles.pillContainer}>
          <PillGroup
            key={selectedTab}
            initialSelectedPillId={selectedTab}
            pillConfigs={payOverTimeTypePillsData}
            onSelection={(externalData: ExternalTypeTabProps) => {
              setSelectedTab(externalData.id);
            }}
          />
          {selectedTab === historyPill ? (
            <HistoryTab closedLoans={closedLoans ?? []} />
          ) : (
            <CurrentLoansTab openLoans={openLoans ?? []} />
          )}
        </View>
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  balanceContainer: {
    alignItems: 'center',
    padding: theme.spacing.m,
  },
  container: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
  sectionDivider: {
    backgroundColor: theme.color.background.surface,
    height: theme.spacing.s,
  },
  pillContainer: {
    marginTop: theme.spacing.s,
  },
});

export default ManageLoans;
